import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MessageCircle, Send, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const formTs = useRef(Math.floor(Date.now() / 1000));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        _website: honeypot,
        _ts: formTs.current,
      });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-16 sm:py-24 lg:py-32 bg-[#0C1620] relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3A4F60]/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 sm:mb-16">
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] text-[#C5993A] mb-3 sm:mb-4">
            Kontakt
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl tracking-tight font-light text-white">
            Jetzt Erstgespräch vereinbaren
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          {/* Left Column: Contact Form + Company Info */}
          <div className="space-y-6 sm:space-y-8" data-testid="contact-form-container">
            {sent ? (
              <div data-testid="contact-success" className="bg-[#12202f] border border-[#C5993A]/30 rounded-lg p-8 sm:p-10 text-center">
                <CheckCircle className="w-12 sm:w-16 h-12 sm:h-16 text-[#C5993A] mx-auto mb-4 sm:mb-6" />
                <h3 className="font-heading text-xl sm:text-2xl text-white mb-3">Nachricht gesendet!</h3>
                <p className="text-[#8899AA] text-sm sm:text-base">Wir melden uns in Kürze bei Ihnen.</p>
                <Button
                  data-testid="send-another-button"
                  onClick={() => { setSent(false); formTs.current = Math.floor(Date.now() / 1000); }}
                  className="mt-6 bg-transparent border border-[#C5993A] text-[#C5993A] hover:bg-[#C5993A]/10 text-sm"
                >
                  Weitere Nachricht senden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-testid="contact-form">
                {/* Honeypot */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                  <label htmlFor="contact_website">Website</label>
                  <input id="contact_website" type="text" name="_website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-[#8899AA] mb-1.5">Name *</label>
                  <Input
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ihr vollständiger Name"
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A] text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-[#8899AA] mb-1.5">E-Mail *</label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ihre@email.de"
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A] text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-[#8899AA] mb-1.5">Telefon</label>
                  <Input
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+49 ..."
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A] text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-[#8899AA] mb-1.5">Nachricht *</label>
                  <Textarea
                    data-testid="contact-message-input"
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Wie können wir Ihnen helfen?"
                    rows={4}
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A] resize-none text-sm sm:text-base"
                  />
                </div>
                {error && <p data-testid="contact-error" className="text-red-400 text-xs sm:text-sm">{error}</p>}
                <Button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium py-5 sm:py-6 text-sm sm:text-base rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Wird gesendet...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Nachricht senden
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Company Info */}
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-5 sm:p-8">
              <h4 className="font-heading text-base sm:text-lg text-white mb-2 sm:mb-3">VMBS UG</h4>
              <div className="text-[#8899AA] text-xs sm:text-sm space-y-1 font-light">
                <p>Hr. Stefan Schäfer</p>
                <p>Ludwigstr. 9</p>
                <p>85298 Scheyern</p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Info + QR */}
          <div className="space-y-4 sm:space-y-8" data-testid="contact-info">
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-5 sm:p-8">
              <h3 className="font-heading text-xl sm:text-2xl text-white mb-4 sm:mb-6">Direkter Kontakt</h3>

              <div className="space-y-4 sm:space-y-5">
                <a href="tel:+491729571525" data-testid="phone-link" className="flex items-center gap-3 sm:gap-4 group">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-sm text-[#8899AA]">Telefon</p>
                    <p className="text-white group-hover:text-[#C5993A] transition-colors text-base sm:text-lg">+49 172 9571525</p>
                  </div>
                </a>

                <a href="mailto:info@vmbs.de" data-testid="email-link" className="flex items-center gap-3 sm:gap-4 group">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-sm text-[#8899AA]">E-Mail</p>
                    <p className="text-white group-hover:text-[#C5993A] transition-colors text-base sm:text-lg">info@vmbs.de</p>
                  </div>
                </a>

                <a href="https://wa.me/4915231495892" data-testid="whatsapp-link" className="flex items-center gap-3 sm:gap-4 group">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-sm text-[#8899AA]">WhatsApp</p>
                    <p className="text-white group-hover:text-[#C5993A] transition-colors text-base sm:text-lg">+49 1523 1495892</p>
                  </div>
                </a>
              </div>
            </div>

            {/* WhatsApp QR Code */}
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-5 sm:p-8 text-center">
              <h4 className="font-heading text-base sm:text-lg text-[#C5993A] mb-3 sm:mb-4">WhatsApp QR-Code scannen</h4>
              <div className="bg-white rounded-lg p-3 sm:p-4 inline-block">
                <img
                  src="/images/whatsapp-qr.png"
                  alt="WhatsApp QR-Code für VMBS"
                  className="w-36 h-36 sm:w-48 sm:h-48 object-contain"
                  data-testid="whatsapp-qr-code"
                />
              </div>
              <p className="text-[#8899AA] text-xs sm:text-sm mt-3 sm:mt-4">
                Scannen Sie den Code mit Ihrem Smartphone
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
